<template>
  <v-card>
    <v-app-bar app clipped-left elevation="0" class="topbar">
      <div class="header-inner">
        <div class="left">
          <!-- Botão de menu para mobile -->
          <v-btn
            v-if="isMobile"
            icon
            @click="toggleNav"
            class="mobile-menu-btn"
          >
            <v-icon>mdi-menu</v-icon>
          </v-btn>
          <v-toolbar-title class="brand brand-lg">
            <router-link to="/" class="brand-link">
              <img
                src="/img/logo-clutcharena.webp"
                alt="Clutch Arena Logo"
                class="brand-logo"
              />
              <span class="brand-text">CLUTCH ARENA</span>
            </router-link>
          </v-toolbar-title>
        </div>

        <nav v-if="!isMobile" class="main-nav">
          <v-btn text small class="nav-btn" to="/inventory">
            <v-icon left>mdi-package-variant-closed</v-icon>
            Inventário
          </v-btn>
          <v-btn text small class="nav-btn" to="/plans">
            <v-icon left>mdi-crown</v-icon>
            VIP
            <v-chip x-small class="vip-badge-inline">35% OFF</v-chip>
          </v-btn>
          <v-btn text small class="nav-btn" to="/ranking">
            <v-icon left>mdi-trophy-outline</v-icon>
            Ranking
          </v-btn>
          <v-btn text small class="nav-btn" to="/FAQ">
            <v-icon left>mdi-help-circle-outline</v-icon>
            FAQ
          </v-btn>
        </nav>
        <div class="right">
          <v-btn
            v-if="user.id == null"
            :href="apiUrl + '/auth/steam'"
            rounded
            class="login-btn simple"
            small
          >
            <v-icon left>mdi-login</v-icon>
            <span class="login-text">Login via Steam</span>
          </v-btn>
          <v-menu
            v-if="user.id !== null"
            bottom
            offset-y
            nudge-top="-4"
            origin="center center"
            transition="scale-transition"
            elevation="0"
            content-class="user-popover"
          >
            <template v-slot:activator="{ on, attrs }">
              <div class="user-menu">
                <v-btn v-on="on" v-bind="attrs" class="vip-badge" depressed>
                  <div class="vip-user-info">
                    <span class="vip-text">{{ truncatedName }}</span>
                    <span v-if="user.vip?.isVIP" class="vip-subtext-vip">
                      <v-icon dense small>mdi-crown</v-icon> VIP
                    </span>
                    <span v-else class="vip-subtext">MEMBRO</span>
                  </div>
                  <v-avatar size="32">
                    <img :src="user.small_image" />
                  </v-avatar>
                </v-btn>
              </div>
            </template>

            <v-list dense>
              <v-list-item :to="'/user/' + user.id">
                <v-list-item-icon>
                  <v-icon>mdi-account</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{
                    $t("Navbar.Profile")
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item :href="apiUrl + '/logout'">
                <v-list-item-icon>
                  <v-icon>mdi-logout</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{
                    $t("Navbar.Logout")
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </v-app-bar>
    <!-- Drawer fixo no desktop, sem blur e com mini-variant (colapsado) -->
    <v-navigation-drawer
      v-model="drawer"
      :permanent="!isMobile"
      :temporary="isMobile"
      :mini-variant="miniVariant"
      app
      clipped
      class="sidebar"
    >
      <!-- Cabeçalho do drawer com botão de alternância (menu no canto esquerdo) -->
      <div class="drawer-header">
        <v-btn icon small @click.stop="toggleNav">
          <v-icon>{{ miniVariant ? "mdi-menu" : "mdi-menu-open" }}</v-icon>
        </v-btn>
        <span class="drawer-title" v-show="!miniVariant">Menu</span>
      </div>
      <v-divider class="drawer-divider"></v-divider>
      <v-list dense nav>
        <v-list-item-group active-class="drawer-active">
          <v-list-item index="Home" :to="'/'" @click="handleMenuItemClick">
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-home</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("Navbar.Home") }}</v-list-item-title>
          </v-list-item>

          <v-list-item
            index="Inventory"
            :to="'/inventory'"
            @click="handleMenuItemClick"
          >
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">
                mdi-package-variant-closed
              </v-icon>
            </v-list-item-icon>
            <v-list-item-title>Inventário</v-list-item-title>
          </v-list-item>

          <v-list-item
            index="BuyVip"
            :to="'/plans'"
            @click="handleMenuItemClick"
          >
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-crown</v-icon>
            </v-list-item-icon>
            <v-list-item-title>VIP</v-list-item-title>
          </v-list-item>

          <v-list-item
            index="ServerRental"
            :to="'/aluguel-servidores'"
            @click="handleMenuItemClick"
            class="server-rental-item"
          >
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-server</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="server-rental-title">
              <span class="server-rental-text">Aluguel de Servidores</span>
              <span v-if="!miniVariant" class="menu-section-badge">
                NOVO
              </span>
            </v-list-item-title>
          </v-list-item>

          <v-list-item
            index="Affiliates"
            :to="'/affiliates'"
            @click="handleMenuItemClick"
            class="affiliates-item"
          >
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-handshake</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="server-rental-title">
              <span class="server-rental-text">Afiliados</span>
              <span v-if="!miniVariant" class="menu-section-badge">NOVO</span>
            </v-list-item-title>
          </v-list-item>

          <v-list-item :to="'/ranking'" @click="handleMenuItemClick">
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-trophy-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              {{ $t("Navbar.PlayerLeader") }}
            </v-list-item-title>
          </v-list-item>

          <v-list-item
            index="Matches"
            :to="'/matches'"
            @click="handleMenuItemClick"
          >
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-gamepad-up</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("Navbar.AllMatches") }}</v-list-item-title>
          </v-list-item>

          <!-- <v-list-item
            v-if="user.id != null"
            index="mymatches"
            :to="'/mymatches'"
          >
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-gamepad-down</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("Navbar.MyMatches") }}</v-list-item-title>
          </v-list-item> -->

          <v-list-item
            v-if="
              user.id != null && (user.admin === 1 || user.super_admin === 1)
            "
            index="admin_page"
            :to="'/admin'"
            @click="handleMenuItemClick"
          >
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-shield-crown</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Administração</v-list-item-title>
          </v-list-item>

          <v-list-item :to="'/FAQ'" @click="handleMenuItemClick">
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">
                mdi-help-circle-outline
              </v-icon>
            </v-list-item-icon>
            <v-list-item-title>FAQ</v-list-item-title>
          </v-list-item>

          <!-- <v-list-item v-if="user.id != null" :to="'/myteams'">
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-spider-web</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("Navbar.MyTeams") }}</v-list-item-title>
          </v-list-item>

          <v-list-item :to="'/teams'" exact>
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-bulletin-board</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("Navbar.AllTeams") }}</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="user.id != null" :to="'/teams/create'" exact>
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-bullseye-arrow</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("Navbar.CreateTeam") }}</v-list-item-title>
          </v-list-item> -->
          <!-- <v-list-item
            v-if="
              user.id != null && (user.admin === 1 || user.super_admin === 1)
            "
            :to="'/myseasons'"
          >
            <v-list-item-icon>
              <v-icon dense color="gray darken-2">mdi-seal</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("Navbar.MySeasons") }}</v-list-item-title>
          </v-list-item> -->
        </v-list-item-group>
      </v-list>
    </v-navigation-drawer>
    <ServerDialog
      v-model="newDialog"
      :serverInfo="{}"
      :title="$t('MyServers.New')"
    />
    <LoginDialog v-model="loginDialog" :title="$t('Login.title')" />
  </v-card>
</template>
<script>
import ServerDialog from "./ServerDialog";
import LoginDialog from "./LoginDialog";
import { mapState } from "vuex";

export default {
  name: "Navbar",
  components: {
    ServerDialog,
    LoginDialog
  },
  computed: {
    ...mapState(["user"]),
    truncatedName() {
      return this.user.name.length > 10
        ? this.user.name.substring(0, 10) + "..."
        : this.user.name;
    }
  },
  data() {
    return {
      isMobile: false,
      drawer: false,
      miniVariant: true, // Inicia colapsado por padrão
      userCollapsedDesktop: true, // Controla se o usuário colapsou manualmente no desktop

      newDialog: false,
      loginDialog: false,
      apiUrl: process.env?.VUE_APP_G5V_API_URL || "/api"
    };
  },
  mounted() {
    // Carrega a preferência salva do usuário
    const savedCollapsedState = localStorage.getItem("navbar-collapsed");
    if (savedCollapsedState !== null) {
      this.userCollapsedDesktop = JSON.parse(savedCollapsedState);
      this.miniVariant = this.userCollapsedDesktop;
    }

    this.updateMobileView();
    window.addEventListener("resize", this.updateMobileView);

    // Configuração inicial baseada no tamanho da tela
    if (!this.isMobile) {
      this.drawer = true;
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateMobileView);
  },
  methods: {
    updateMobileView() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 960;

      // Só aplica mudanças se houve TRANSIÇÃO entre mobile/desktop
      if (wasMobile !== this.isMobile) {
        if (this.isMobile) {
          // Mudou para mobile: fecha drawer, remove mini-variant
          this.drawer = false;
          this.miniVariant = false;
        } else {
          // Mudou para desktop: abre drawer, restaura preferência
          this.drawer = true;
          this.miniVariant = this.userCollapsedDesktop;
        }
      }
      // Se não houve transição, mantém estados como estão
    },
    redirectTo(route) {
      this.$router.push(route);
    },
    toggleNav() {
      if (this.isMobile) {
        this.drawer = !this.drawer;
      } else {
        this.miniVariant = !this.miniVariant;
        this.userCollapsedDesktop = this.miniVariant;
        localStorage.setItem(
          "navbar-collapsed",
          JSON.stringify(this.userCollapsedDesktop)
        );
      }
    },
    handleMenuItemClick() {
      // Se está no mobile, fecha o menu após navegar
      if (this.isMobile) {
        this.drawer = false;
      }
    },
    goToWarmup() {
      this.drawer = false;
      if (this.$route.path !== "/") {
        this.$router.push({ path: "/", hash: "#warmup" });
        return;
      }
      const el = document.getElementById("warmup");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
};
</script>

<style scoped>
.header-inner {
  box-sizing: border-box;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  padding: 0 24px;
  gap: 20px;
}
.left {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: start;
}
.right {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: end;
}
.brand {
  font-family: "Orbitron", system-ui, sans-serif;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #ff711a;
}
.brand-lg {
  font-size: 22px;
}
.brand-link {
  color: #ff711a !important;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: color 0.2s ease, text-shadow 0.2s ease;
}
.brand-link:visited {
  color: #ff711a !important;
}
.brand-link:hover {
  color: #ff711a !important;
  /* Removido scale para evitar "pulo" e recorte nas bordas */
  text-shadow: 0 0 8px rgba(255, 113, 26, 0.35);
}
.brand-link:active {
  color: #ff711a !important;
}

.brand-logo {
  height: 32px;
  width: auto;
  filter: drop-shadow(0 2px 4px rgba(255, 113, 26, 0.3));
  transition: filter 0.2s ease;
}

.brand-link:hover .brand-logo {
  filter: drop-shadow(0 4px 10px rgba(255, 113, 26, 0.5));
}

.brand-text {
  font-family: "Orbitron", system-ui, sans-serif;
  font-weight: 800;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.main-nav {
  display: flex;
  align-items: center;
  gap: 20px;
  justify-self: center;
}
.nav-btn {
  color: hsl(var(--foreground) / 0.7) !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  margin: 0 2px;
  font-size: 14px;
  height: 36px;
  border-radius: 8px;
  padding: 0 10px;
  transition: background-color 160ms ease, box-shadow 160ms ease,
    color 160ms ease;
}
.nav-btn .v-icon {
  font-size: 18px !important;
  margin-right: 6px;
}
.nav-btn:hover {
  background-color: rgba(255, 255, 255, 0.06) !important;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.08) inset,
    0 0 12px rgba(255, 255, 255, 0.06);
  color: #ff711a !important;
}
.nav-btn:hover .v-icon {
  color: #ff711a !important;
}
.cta-btn {
  background: linear-gradient(
    135deg,
    rgba(255, 153, 0, 0.9),
    rgba(255, 106, 0, 0.9)
  ) !important;
  color: #111 !important;
  font-weight: 700;
}
.vip-badge-inline {
  margin-left: 6px;
  background: #ffc107 !important;
  color: #111 !important;
  height: 18px;
  line-height: 18px;
  font-size: 10px !important;
  padding: 0 6px;
}
.topbar {
  background: #0f0f10 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  min-height: 64px !important;
  z-index: 1006 !important;
}
.spacer-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.14);
}
.sidebar {
  background: #151619 !important;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  z-index: 1005 !important;
}
/* Drawer agora é clipped ao app-bar; não precisamos ajustar top/height manualmente */
.drawer-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
}
.drawer-title {
  font-weight: 700;
  color: #e6e6e6;
}
.drawer-divider {
  margin: 0 8px 8px;
}
.drawer-active .v-list-item__title,
.drawer-active .v-icon,
.v-list-item--active .v-list-item__title,
.v-list-item--active .v-icon,
.v-list-item.router-link-active .v-list-item__title,
.v-list-item.router-link-active .v-icon,
.v-list-item.router-link-exact-active .v-list-item__title,
.v-list-item.router-link-exact-active .v-icon {
  color: #ff711a !important;
}
.v-list-item:hover .v-list-item__title,
.v-list-item:hover .v-icon {
  color: #ff711a !important;
}
.icon-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 8px; /* Espaçamento entre ícones */
  cursor: pointer; /* Indica que é clicável */
  transition: transform 0.2s ease-in-out;
}

.icon-link:hover {
  transform: scale(1.1); /* Efeito de "zoom" ao passar o mouse */
}

.icon-image {
  height: 20px; /* Tamanho uniforme para imagens */
  width: 20px;
  filter: brightness(0.6); /* Cor cinza escura */
  transition: filter 0.2s ease-in-out;
}

.icon {
  font-size: 20px; /* Tamanho consistente para ícones */
  color: #979696; /* Cinza escuro */
  transition: color 0.2s ease-in-out;
}

.icon:hover {
  color: #a6a6a6; /* Clareia no hover */
}

.v-application .primary--text {
  color: #a6a6a6 !important;
  caret-color: #424242 !important;
}
.login-btn {
  background: #ff9900 !important;
  color: #0f0f10 !important;
  font-family: "Orbitron", system-ui, sans-serif;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: none;
  padding: 8px 14px;
  border: none !important;
}
.login-btn .v-icon {
  font-size: 18px !important;
  margin-right: 6px;
}
.login-btn:hover {
  filter: brightness(1.03);
  box-shadow: 0 0 0 1px rgba(255, 153, 0, 0.4) inset,
    0 0 14px rgba(255, 153, 0, 0.22);
}

.login-text {
  text-transform: none;
}
.login-btn.simple {
  box-shadow: 0 0 20px rgba(255, 153, 0, 0.15);
}

.login-icon {
  color: #111 !important;
  background: #fff;
  border-radius: 6px;
  padding: 2px;
}

.logo-link {
  display: inline-block; /* Faz o link ocupar apenas o espaço necessário */
  cursor: pointer; /* Muda o cursor para a mão ao passar sobre o link */
  text-decoration: none; /* Remove o sublinhado do link */
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.fade-in {
  animation: fadeIn 2s ease-out;
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 8px;
}

.vip-badge {
  display: flex;
  align-items: right;
  justify-content: space-between; /* Espaçamento uniforme */
  background-color: black !important;
  border: 1px solid #ff711a;
  border-radius: 8px;
  font-weight: bold;
  color: white;
  height: 45px !important;
  padding: 5px 10px;
  min-width: 130px; /* Garante que o botão tenha um tamanho fixo */
  animation: none !important; /* Remove qualquer animação */
}

/* Força o botão do usuário a não herdar o fundo cinza padrão do Vuetify em tema dark */
.theme--dark.v-btn.v-btn--has-bg.vip-badge {
  background-color: black !important;
}

.vip-badge:hover {
  box-shadow: 0 0 0 1px rgba(255, 113, 26, 0.35),
    0 0 12px rgba(255, 113, 26, 0.18);
  animation: none !important; /* Remove qualquer animação no hover */
}

.vip-text {
  color: white;
  font-weight: bold;
  font-size: 0.9rem;
  white-space: nowrap; /* Evita quebra de linha no nome */
  margin-right: 14px;
}

.vip-subtext-vip,
.vip-subtext {
  font-style: italic;
  font-size: 0.7rem;
  margin-right: 14px;
}

.vip-subtext-vip {
  color: rgba(255, 153, 0, 0.8);
}

.vip-subtext {
  color: rgba(255, 94, 0, 0.801);
}

.vip-badge v-avatar {
  margin-left: auto;
}

.user-info-list {
  background: #121212; /* Fundo escuro do painel */
  padding: 10px;
  border-radius: 8px;
}

/* Popover do usuário (menu) - Redesign completo */
.user-popover {
  background: #1a1b1e !important;
  border: 1px solid rgba(255, 113, 26, 0.15) !important;
  border-radius: 8px !important;
  padding: 4px !important;
  min-width: 160px;
  max-width: 180px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset,
    0 4px 16px rgba(255, 113, 26, 0.12) !important;
  overflow: hidden;
}
/* Lista - layout moderno e limpo */
.user-popover >>> .v-list {
  background: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Itens do menu - design clean e responsivo */
.user-popover >>> .v-list-item {
  min-height: 32px !important;
  height: 32px !important;
  border-radius: 6px !important;
  margin: 1px !important;
  padding: 0 8px !important;
  transition: all 200ms cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  position: relative !important;
  overflow: hidden !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
}

/* Remove overlay padrão do Vuetify */
.user-popover >>> .v-list-item--link:before,
.user-popover >>> .v-list-item--link:after {
  display: none !important;
}

/* Estado hover com feedback visual sofisticado */
.user-popover >>> .v-list-item:hover {
  background: linear-gradient(
    135deg,
    rgba(255, 113, 26, 0.08) 0%,
    rgba(255, 113, 26, 0.04) 100%
  ) !important;
  box-shadow: 0 0 0 1px rgba(255, 113, 26, 0.2) inset !important;
  transform: translateX(2px) !important;
}

/* Ícones - alinhamento perfeito */
.user-popover >>> .v-list-item__icon {
  min-width: 16px !important;
  width: 16px !important;
  height: 16px !important;
  margin-right: 8px !important;
  margin-left: 0 !important;
  flex-shrink: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.user-popover >>> .v-list-item .v-icon {
  font-size: 14px !important;
  color: #a3a3a3 !important;
  transition: color 200ms ease !important;
  line-height: 1 !important;
}

/* Conteúdo e título */
.user-popover >>> .v-list-item__content {
  padding: 0 !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  flex: 1 !important;
  height: 32px !important;
}

.user-popover >>> .v-list-item__title {
  font-weight: 500 !important;
  color: #e6e6e6 !important;
  font-size: 12px !important;
  letter-spacing: 0.2px !important;
  line-height: 1 !important;
  white-space: nowrap !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* Estados hover - cores coordenadas */
.user-popover >>> .v-list-item:hover .v-list-item__title {
  color: #ff711a !important;
  font-weight: 600 !important;
}

.user-popover >>> .v-list-item:hover .v-icon {
  color: #ff711a !important;
  transform: scale(1.05) !important;
}

/* Estado ativo (se aplicável) */
.user-popover >>> .v-list-item--active {
  background: rgba(255, 113, 26, 0.12) !important;
}

.user-popover >>> .v-list-item--active .v-list-item__title,
.user-popover >>> .v-list-item--active .v-icon {
  color: #ff711a !important;
}

.user-info {
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
}

.user-avatar {
  border: 2px solid rgba(255, 153, 0, 0.8);
  border-radius: 50%;
  width: 50px;
  height: 50px;
}

.user-name {
  font-weight: bold;
  font-size: 1rem;
  color: white;
  margin-bottom: 4px;
}

.user-vip-status {
  display: flex;
  margin-top: 4px;
}

.vip-chip {
  font-weight: bold;
  margin-left: 10px;
}

.vip-active {
  color: rgba(255, 153, 0, 0.8);
  font-style: italic;
  font-size: 0.7rem;
}

.vip-inactive {
  color: rgba(255, 94, 0, 0.801);
  font-style: italic;
  font-size: 0.6rem;
}

/* Botão mobile menu */
.mobile-menu-btn {
  color: #ffffff !important;
  margin-right: 8px;
}

.mobile-menu-btn:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

/* ============================================
   ALUGUEL DE SERVIDORES
   ============================================ */

.server-rental-title {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
  flex-wrap: nowrap;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.menu-section-badge {
  display: inline-block;
  background: rgba(255, 153, 0, 0.1);
  color: #ff9900;
  padding: 1px 4px;
  border-radius: 6px;
  font-size: 7px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid rgba(255, 153, 0, 0.3);
  margin-left: 4px;
  animation: pulse-orange 2s infinite;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1;
}

/* Remove completamente a badge no modo colapsado */
.v-navigation-drawer--mini-variant .menu-section-badge {
  display: none !important;
}

@keyframes pulse-orange {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 2px 8px rgba(255, 153, 0, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(255, 153, 0, 0.5);
  }
}

/* Keyframe removida pois não é mais necessária */

.server-rental-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.server-rental-item:hover .menu-section-badge {
  animation: none;
  transform: scale(1.1);
  background: rgba(255, 153, 0, 0.2);
}

/* Responsividade do drawer */
@media (max-width: 960px) {
  .sidebar {
    z-index: 1005 !important;
  }

  .topbar {
    z-index: 1006 !important;
  }
}

/* Garante que o sidebar fique sempre acima do conteúdo */
@media (min-width: 961px) {
  .sidebar {
    z-index: 1005 !important;
  }

  .topbar {
    z-index: 1006 !important;
  }

  /* Força elementos da página a ficarem abaixo do sidebar */
  body >>> .v-main,
  body >>> .v-application,
  body >>> section,
  body >>> .page-content,
  body >>> .content-container {
    z-index: 1 !important;
    position: relative;
  }

  /* Especificamente para seções VIP que podem ter z-index alto */
  body >>> .vip-section,
  body >>> .hero-section,
  body >>> .banner-section {
    z-index: 1 !important;
    position: relative;
  }

  /* Regra geral para prevenir elementos de sobreporem o sidebar */
  body >>> *:not(.v-navigation-drawer):not(.v-app-bar):not(.v-overlay) {
    z-index: auto !important;
  }

  /* Força qualquer elemento posicionado a respeitar o sidebar */
  body >>> [style*="z-index"]:not(.v-navigation-drawer):not(.v-app-bar) {
    z-index: 1 !important;
  }
}

/* Responsividade do Header */
@media (max-width: 1024px) {
  .header-inner {
    padding: 0 16px;
    gap: 16px;
  }

  .main-nav {
    gap: 16px;
  }

  .brand-logo {
    height: 28px;
  }

  .brand-link {
    gap: 8px;
  }
}

@media (max-width: 768px) {
  .header-inner {
    padding: 0 12px;
    gap: 12px;
  }

  .brand-logo {
    height: 24px;
  }

  .brand-text {
    font-size: 18px;
  }
}

/* Ajustes para o brand no mobile */
@media (max-width: 600px) {
  .header-inner {
    padding: 0 8px;
    gap: 8px;
  }

  .brand {
    font-size: 16px !important;
  }

  .brand-link {
    font-size: 16px !important;
    gap: 6px;
  }

  .brand-logo {
    height: 20px;
  }

  .brand-text {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .header-inner {
    padding: 0 6px;
  }

  .brand-link {
    gap: 4px;
  }

  .brand-logo {
    height: 18px;
  }

  .brand-text {
    font-size: 14px;
    letter-spacing: 0.01em;
  }
}

.vip-user-info {
  display: flex;
  flex-direction: column; /* Coloca nome e status um abaixo do outro */
  align-items: center; /* Centraliza horizontalmente */
  text-align: center; /* Garante que o texto fique bem centralizado */
  flex-grow: 1; /* Permite que o nome e status ocupem mais espaço */
}

.v-avatar {
  flex-shrink: 0; /* Evita que o avatar seja reduzido pelo flexbox */
}
</style>
